import { useMemo, useState } from "react";
import { SortData } from "../DesignUploadStep.types";

export function useStructureTableSort<T>(
  rowState: any[],
  initialSortState: SortData<T> | null = null,
) {
  const [sortState, setSortState] = useState<SortData<T> | null>(initialSortState);

  const getComparableValue = (value: string | number | boolean | undefined) => {
    if (typeof value === "boolean") return value ? 1 : 0;
    if (typeof value === "number") return value;
    return String(value ?? "")
      .trim()
      .toLowerCase();
  };

  const handleSort = (key: T) => {
    setSortState((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }

      return { key, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    const rows = rowState;
    if (!sortState) return rows;

    const { key, direction } = sortState;
    const multiplier = direction === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      const leftValue = getComparableValue(
        (a as Record<string, string | number | boolean | undefined>)[key],
      );
      const rightValue = getComparableValue(
        (b as Record<string, string | number | boolean | undefined>)[key],
      );

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return (leftValue - rightValue) * multiplier;
      }

      return String(leftValue).localeCompare(String(rightValue)) * multiplier;
    });
  }, [rowState, sortState]);

  return { sortedRows, handleSort, sortState };
}
